import { defineStore } from 'pinia';
import { useStore } from 'vuex';
import { getUserInfoAPI, getNonReadAlarmExistAPI } from '@/api/myPage';
import { addUserSelectOptionAPI, getUserSelectOptionAPI, setUserSelectOptionAPI } from '@/api/userSelectOption';
import { DEV_MODE } from '@/config/dev-mode';

const systemId = import.meta.env.VITE_APP_SYSTEMID;

export const useMyInfoStore = defineStore('myinfo', () => {
  const store = useStore()

  const myInfo = reactive({
    name: '',
    nickname: '',
    isUseNickname: false,
    displayName: '',
    /**
     * 제휴 파트너 정보. API `/users/{systemId}/mobile/get` 응답의 `partner` 그대로 보관.
     * - 일반 회원: null
     * - 제휴 회원: { provider: string, totalPoint: number }
     */
    partner: null,
  });

  /** 이름 또는 닉네임 중 선택된 항목 */
  const displayName = computed(() => {
    // return myInfo.isUseNickname ? myInfo.nickname :
    return myInfo.name;
  });

  /** 제휴 파트너 정보 (편의 computed) */
  const partner = computed(() => myInfo.partner);

  /** 복지찬스(SmartWel) 제휴 회원 여부 (편의 computed) */
  const isSmartwelMember = computed(() => myInfo.partner?.provider === 'smartwel');

  /** 사용자 선택 옵션중 서버 입력값 */
  const defaultOptionData = {
    createdAt: null,
    createdBy: null,
    updatedAt: null,
    updatedBy: null,
  }
  /** 사용자 선택 옵션중 사용자 입력값 */
  const defaultOptions = {
    systemId,
    /** 한국시 적용 여부 (적용 true) */
    isKoreaTime: true,
    /** 조자시, 야자시 여부 (적용: true) */
    earlyTime: true,
    /** 생일 절기(동지, 입춘, 모름) ('spring' | 'winter' | 'unknown') */
    birthType: 'spring',
    /** 역학달력 간지(한글표시: true) */
    useGanJi: true,
    /** 역학달력 음력(표시: true) */
    useLuna: true,
    /** 역학달력 절기(표시: true */
    useTwentyFourSeason: true,
    /** 역학달력 공휴일(표시: true) */
    useHolidays: true,
    /** 언어 표기법 / KOR: 한글 표기 / HANJA: 한자 표기 / MIX: 한자+한글 표기 */
    langType: 'MIX',
    /** 테마 선택 / BACKGROUND: 배경컬러 / TEXT: 글자컬러 / BLACKWHITE: 글자흑백 */
    fiveElementTheme: 'BACKGROUND',
    /** 원국 표기 - HHDDMMYYYY: 시일월년 순 / YYYYMMDDHH: 년월일시 순 */
    wonGukSort: 'HHDDMMYYYY',
  };

  /** 사용자 선택 옵션 */
  const selectedOptions = reactive({
    ...defaultOptionData,
    ...defaultOptions,
  });

  const setMyInfo = ({name, nickname, isUseNickname, partner}) => {
    myInfo.name = name;
    myInfo.nickname = nickname;
    myInfo.isUseNickname = isUseNickname;
    if (partner !== undefined) {
      myInfo.partner = partner ?? null;
    }
  };

  /** 사용자 선택 옵션 업데이트 - selectedOptions */
  const setSelectedOptions = (userSelectOption) => {
    Object.assign(selectedOptions, {
      ...defaultOptions,
      ...userSelectOption,
    });    
  };

  const fetchMyInfo = async () => {
    try {
      const result = await getUserInfoAPI();

      if (result) {
        setMyInfo({
          name: result.name,
          nickname: result.nickname,
          isUseNickname: result.isUseNickname,
          partner: result.partner ?? null,
        });

        // checkUserSelectOptions(result.id);
      } else {
        setMyInfo({
          name: '회원',
          nickname: '',
          isUseNickname: false,
        });
      };
    } catch (e) {
      DEV_MODE && console.log('useMyInfoStore fetchMyInfo error:', e);
    }
  };

  /** 사용자 옵션 유무 확인 및 업데이트 */
  const checkUserSelectOptions = async (id) => {
    if (!id) return;

    try {
      const savedOptions = await getUserSelectOptionAPI(id);      
      if (savedOptions) setSelectedOptions(savedOptions);
      else addUserSelectOption();
    } catch (e) {
      DEV_MODE && console.error('check option error:', e);
    };
  };

  /** 사용자(회원) 옵션 생성 */
  const addUserSelectOption = async () => {
    const data = { ...defaultOptions };
    try {
      const savedOptions = await addUserSelectOptionAPI(data);
      if (savedOptions) setSelectedOptions(savedOptions);    
    } catch (e) {
      DEV_MODE && console.error('add option error:', e);
    };
  };

  /** 사용자(회원) 옵션 업데이트 */
  const setUserSelectOption = async (key, value) => {
    // const userData = store.getters['user/id']
    const data = {
      ...selectedOptions,
      [key]: value,
    }
    setSelectedOptions(data)

    // if (!userData) {
    //   setSelectedOptions(data)
    // } else {
    //   try {
    //     const savedOptions = await setUserSelectOptionAPI(data);
    //     if (savedOptions) setSelectedOptions(savedOptions);
    //   } catch (e) {
    //     DEV_MODE && console.error('update option error:', e)
    //   }
    // }
  };

  /** 읽지 않은 알림 존재 여부 */
  const hasUnreadAlarm = ref(false);

  const fetchUnreadAlarm = async () => {
    try {
      const result = await getNonReadAlarmExistAPI();
      hasUnreadAlarm.value = !!result;
    } catch {
      hasUnreadAlarm.value = false;
    }
  };

  const resetSelectedOption = () => {
    Object.assign(selectedOptions, {
      ...defaultOptionData,
      ...defaultOptions,
    });
  };

  /**
   * 로그아웃/탈퇴 시 회원 정보 전체 초기화.
   * - myInfo(name, nickname, partner 등) 리셋 → 헤더의 smartwel 로고, 드로어의 복지찬스
   *   포인트 카드 등 제휴 회원 UI 잔존 문제 방지.
   * - hasUnreadAlarm, selectedOptions도 함께 초기화하여 다음 사용자에게 이전 상태 노출 방지.
   */
  const resetMyInfo = () => {
    myInfo.name = '';
    myInfo.nickname = '';
    myInfo.isUseNickname = false;
    myInfo.partner = null;
    hasUnreadAlarm.value = false;
    resetSelectedOption();
  };

  return {
    myInfo,
    displayName,
    partner,
    isSmartwelMember,
    selectedOptions,
    setMyInfo,
    fetchMyInfo,
    checkUserSelectOptions,
    setUserSelectOption,
    hasUnreadAlarm,
    fetchUnreadAlarm,
    resetSelectedOption,
    resetMyInfo,
  };
});